
import { Component, Prop, Watch, Vue } from 'vue-property-decorator';
import { TranslateResult } from 'vue-i18n';
import { namespace } from 'vuex-class';

interface VideoDimensions {
  width: number;
  height: number;
}

const foodcard = namespace('foodcard');

@Component
export default class VVideoUpload extends Vue {
  @Prop({ type: Boolean, default: false }) public disabled?: boolean;
  @Prop({ type: Array, default: () => [] }) public errorMessages?: any[];
  @Prop({ type: String }) public name?: string | TranslateResult;
  @Prop({ type: String, default: 'video/*' }) public accept?: string;
  @Prop({ type: String, default: 'Upload Video' }) public label?: string;
  @Prop({ type: String, default: '' }) public hint!: string;

  @foodcard.Action('uploadArticleBannerVideo') public uploadBannerVideo!: any;

  public $refs!: {
    fileInput: any;
    videoPlayer: HTMLVideoElement;
  };

  public video: File | null = null;
  public videoPreview: string = '';
  public videoDuration: number = 0;
  public videoDimensions: VideoDimensions = { width: 0, height: 0 };
  public showDialog: boolean = false;
  public isManualUpload: boolean = false;
  public showNotAVideoAlert: boolean = false;
  public uploadInProgress: boolean = false;
  public uploadProgress: number = 0;
  public uploadedBytes: number = 0;
  public totalBytes: number = 0;
  public uploadComplete: boolean = false;
  public progressInterval: number | null = null;

  public onInputClick(event: any) {
    if (!this.isManualUpload) {
      event.preventDefault();
      this.showDialog = true;
    }
  }

  public manualUpload() {
    this.isManualUpload = true;
    this.$refs?.fileInput?.$refs?.input?.click();
    this.isManualUpload = false;
  }

  public dragOver(event: any) {
    event.dataTransfer.dropEffect = 'copy';
  }

  public dragLeave(event: any) {
    event.dataTransfer.dropEffect = 'none';
  }

  public dropFile(event: any) {
    const droppedFiles = event.dataTransfer.files;

    if (!droppedFiles?.length) {
      return;
    }

    if (!this.testVideos(droppedFiles)) {
      this.showNotAVideoAlert = true;
      setTimeout(() => {
        this.showNotAVideoAlert = false;
      }, 1500);
      return;
    }

    this.video = droppedFiles[0];
    this.createVideoPreview(droppedFiles[0]);
    this.showDialog = false;
    this.uploadComplete = false;
  }

  public testVideos(files: FileList): boolean {
    for (const file of files) {
      if (!file.type.match('video.*')) {
        return false;
      }
    }
    return true;
  }

  public createVideoPreview(file: File) {
    if (this.videoPreview) {
      URL.revokeObjectURL(this.videoPreview);
    }

    this.videoPreview = URL.createObjectURL(file);
    this.totalBytes = file.size;
  }

  public onVideoLoaded() {
    if (this.$refs.videoPlayer) {
      this.videoDuration = this.$refs.videoPlayer.duration;
      this.videoDimensions = {
        width: this.$refs.videoPlayer.videoWidth,
        height: this.$refs.videoPlayer.videoHeight,
      };
    }
  }

  public formatDuration(duration: number): string {
    const minutes = Math.floor(duration / 60);
    const seconds = Math.floor(duration % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }

  public formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  public async startUpload() {
    if (!this.video) return;

    this.uploadInProgress = true;
    this.uploadProgress = 0;
    this.uploadedBytes = 0;
    this.uploadComplete = false;

    this.startProgressSimulation();

    try {
      const formData = new FormData();
      formData.append('video', this.video as Blob, this.video.name);

      await this.uploadBannerVideo({
        video: formData,
      });

      this.stopProgressSimulation();
      this.uploadProgress = 100;
      this.uploadedBytes = this.totalBytes;
      this.uploadComplete = true;
      this.$emit('upload-complete', {
        file: this.video,
        name: this.video.name,
        size: this.video.size,
        duration: this.videoDuration,
        dimensions: this.videoDimensions,
      });
    } catch (error) {
      this.stopProgressSimulation();
      this.uploadComplete = false;
      this.handleUploadError(error);
    } finally {
      this.uploadInProgress = false;
    }
  }

  public startProgressSimulation() {
    let currentProgress = 0;

    this.progressInterval = window.setInterval(() => {
      currentProgress += Math.random() * 5;

      if (currentProgress >= 95) {
        currentProgress = 95;
        this.stopProgressSimulation();
      }

      this.uploadProgress = currentProgress;
      this.uploadedBytes = (this.totalBytes * currentProgress) / 100;
    }, 200);
  }

  public stopProgressSimulation() {
    if (this.progressInterval) {
      clearInterval(this.progressInterval);
      this.progressInterval = null;
    }
  }

  public handleUploadError(error: any) {
    let errorMessage = 'Upload failed';
    let statusCode = -1;

    if (error.response) {
      errorMessage = error.response.data.message || 'Server error';
      statusCode = error.response.status;
    } else if (error.request) {
      errorMessage = 'Network error. Please check your connection.';
      statusCode = 0;
    } else {
      errorMessage = error.message || 'Upload failed';
    }

    this.$emit('upload-error', {
      message: errorMessage,
      status: statusCode,
      originalError: error,
    });
  }

  public clearVideo() {
    this.video = null;
    if (this.videoPreview) {
      URL.revokeObjectURL(this.videoPreview);
      this.videoPreview = '';
    }
    this.videoDuration = 0;
    this.videoDimensions = { width: 0, height: 0 };
    this.uploadProgress = 0;
    this.uploadedBytes = 0;
    this.totalBytes = 0;
    this.uploadComplete = false;
    this.stopProgressSimulation();
    this.$emit('input', null);
    this.$emit('change', null);
    this.$emit('video-selected', null);
  }

  @Watch('video')
  public onVideoChange(newVideo: File | null, oldVideo: File | null) {
    this.showDialog = false;

    if (oldVideo && this.videoPreview) {
      URL.revokeObjectURL(this.videoPreview);
    }

    if (newVideo) {
      this.createVideoPreview(newVideo);
      this.uploadComplete = false;
      this.$emit('video-selected', {
        file: newVideo,
        name: newVideo.name,
        size: newVideo.size,
        type: newVideo.type,
      });
    } else {
      this.videoPreview = '';
      this.videoDuration = 0;
      this.videoDimensions = { width: 0, height: 0 };
      this.uploadComplete = false;
    }

    this.$emit('input', this.video);
    this.$emit('change', this.video);
  }

  public beforeDestroy() {
    if (this.videoPreview) {
      URL.revokeObjectURL(this.videoPreview);
    }
    this.stopProgressSimulation();
  }
}
